import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { YearlySimulationResult, SimulationDistribution } from './types';

interface SimulationChartsProps {
  yearlyResults: YearlySimulationResult[];
  distribution: SimulationDistribution;
  activeView: 'summary' | 'yearly' | 'distribution';
}

export function SimulationCharts({
  yearlyResults,
  distribution,
  activeView
}: SimulationChartsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Create data for portfolio value chart (Growth vs Investment)
  const portfolioData = yearlyResults.map(yr => ({
    year: `Year ${yr.year}`,
    value: yr.portfolioValue,
    invested: yr.totalInvested || 0, // Now purely from simulation data
    benchmark: yr.hysaValue || 0, // 5% HYSA
    return: yr.yearlyReturn,
    profit: yr.portfolioValue - (yr.totalInvested || 0)
  }));

  // Create percentile lines from distribution (Risk Cone)
  // We need to synthesize this because the current data model separates yearly from distribution outcomes
  // Ideally, we'd have yearly percentiles, but for now we will just show the end result distribution or
  // create a linear interpolation for the visual "cone" effect if needed. 
  // BETTER APPROACH: Just show the end-result Probability Curve correctly.

  // Actually, user wants to see VALUE. Let's make Distribution a "Risk Analysis" curve.
  // For now, let's stick to the Histogram but make it look like a Probability Density Function (Curve)
  const histogramData = distribution.histogram.map(bucket => ({
    range: `${formatCurrency(bucket.min)} - ${formatCurrency(bucket.max)}`,
    count: bucket.count,
    mid: (bucket.min + bucket.max) / 2
  }));

  return (
    <div className="h-80 w-full animate-fade-in">
      {activeView === 'summary' && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={portfolioData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
            <XAxis
              dataKey="year"
              tick={{ fill: '#9ca3af', fontSize: 12 }}
              axisLine={{ stroke: '#4b5563' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(val) => `$${val / 1000}k`}
              tick={{ fill: '#9ca3af', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', borderRadius: '0.5rem' }}
              itemStyle={{ color: '#fff' }}
              formatter={(value: number) => formatCurrency(value)}
            />
            <Legend verticalAlign="top" height={36} />

            {/* Investment Basis (Gray) */}
            <Area
              type="monotone"
              dataKey="invested"
              name="Your Investment"
              stackId="1"
              stroke="#6b7280"
              fill="#9ca3af"
              fillOpacity={0.3}
            />

            {/* Benchmark (Green Dashed Line) */}
            <Area
              type="monotone"
              dataKey="benchmark"
              name="Savings Account (5%)"
              stroke="#10b981"
              strokeDasharray="5 5"
              fill="none"
              strokeWidth={2}
            />

            {/* Profit/Growth (Blue) - We stack it? No, simpler to overlay to show total value */}
            {/* Actually standard for this is: Area for Total Value, and a Line or Area below for Investment */}

            <Area
              type="monotone"
              dataKey="value"
              name="Total Portfolio Value"
              stroke="#3b82f6"
              fill="url(#colorValue)"
              strokeWidth={3}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {activeView === 'yearly' && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={portfolioData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
            <XAxis dataKey="year" tick={{ fill: '#9ca3af', fontSize: 12 }} axisLine={{ stroke: '#4b5563' }} />
            <YAxis tickFormatter={(val) => `${val}%`} tick={{ fill: '#9ca3af', fontSize: 12 }} axisLine={false} />
            <Tooltip
              contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', borderRadius: '0.5rem' }}
              formatter={(value: number) => formatPercent(value)}
            />
            <Legend verticalAlign="top" height={36} />
            <Area
              type="step"
              dataKey="return"
              name="Annual Return (%)"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.2}
            />
            <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {activeView === 'distribution' && (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={histogramData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
            <XAxis
              dataKey="range"
              tick={{ fill: '#9ca3af', fontSize: 10 }}
              interval={1}
              angle={-15}
              textAnchor="end"
              height={50}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-gray-900 border border-gray-700 p-3 rounded-lg shadow-xl">
                      <p className="text-purple-400 font-bold mb-1">Probability Zone</p>
                      <p className="text-white text-sm">Value Range: {data.range}</p>
                      <p className="text-gray-400 text-xs mt-1">Likelihood: {data.count} simulations</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend verticalAlign="top" height={36} />
            <Area
              type="monotone"
              dataKey="count"
              name="Outcome Probability Curve"
              stroke="#8b5cf6"
              strokeWidth={3}
              fill="url(#colorCount)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}